import React from 'react';
import { Target, Sparkles, Loader2, AlertCircle, Watch, ChevronDown } from 'lucide-react';
import WearableIntegration from '../WearableIntegration';

const focusAreaOptions = [
  { id: 'weight_loss', label: 'Weight Loss', icon: '\u{1F3C3}' },
  { id: 'weight_gain', label: 'Weight Gain', icon: '\u{1F4AA}' },
  { id: 'diabetes_control', label: 'Diabetes Control', icon: '\u{1FA78}' },
  { id: 'cholesterol_management', label: 'Cholesterol Management', icon: '\u2764\uFE0F' },
  { id: 'hypertension', label: 'Hypertension', icon: '\u{1FAC0}' },
  { id: 'stress_management', label: 'Stress Management', icon: '\u{1F9D8}' },
  { id: 'pcos_management', label: 'PCOS Management', icon: '\u{1F469}' },
  { id: 'thyroid_management', label: 'Thyroid Management', icon: '\u{1F98B}' },
  { id: 'back_pain', label: 'Back Pain Relief', icon: '\u{1F519}' },
  { id: 'sleep_improvement', label: 'Sleep Improvement', icon: '\u{1F634}' },
  { id: 'general_wellness', label: 'General Wellness', icon: '\u2728' },
  { id: 'immunity_boost', label: 'Immunity Boost', icon: '\u{1F6E1}\uFE0F' }
];

export const LifestylePlanForm = ({
  formData, setFormData, loading, error,
  showWearables, setShowWearables, wearableData,
  patientId, fetchWearableData, generatePlan, toggleFocusArea
}) => (
  <>
    {/* Wearable Integration */}
    <div className="mb-6">
      <button onClick={() => setShowWearables(!showWearables)}
        className={`w-full p-4 rounded-xl border transition-all flex items-center justify-between ${
          showWearables || wearableData?.connected_devices > 0
            ? 'bg-emerald-600/20 border-emerald-500'
            : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
        }`}>
        <div className="flex items-center gap-3">
          <Watch className="w-6 h-6 text-emerald-500" />
          <div className="text-left">
            <h3 className="font-semibold">
              {wearableData?.connected_devices > 0
                ? `${wearableData.connected_devices} Device(s) Connected`
                : 'Connect Wearable Devices'}
            </h3>
            <p className="text-sm text-slate-400">
              {wearableData?.connected_devices > 0
                ? 'Using real health data for personalized recommendations'
                : 'Sync Fitbit, Apple Watch, Google Fit for better plans'}
            </p>
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 transition-transform ${showWearables ? 'rotate-180' : ''}`} />
      </button>

      {showWearables && (
        <div className="mt-4 bg-slate-800/30 border border-slate-700 rounded-xl p-6">
          <WearableIntegration
            patientId={patientId || 'demo_user'}
            onDataReady={() => { setShowWearables(false); fetchWearableData(); }}
          />
        </div>
      )}

      {wearableData?.ai_ready_data && !showWearables && (
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Avg Steps', value: wearableData.ai_ready_data.avg_daily_steps?.toLocaleString(), color: 'emerald' },
            { label: 'Avg Sleep', value: `${wearableData.ai_ready_data.avg_sleep_hours}h`, color: 'blue' },
            { label: 'Avg Heart Rate', value: `${wearableData.ai_ready_data.avg_heart_rate} bpm`, color: 'red' },
            { label: 'Activity Level', value: wearableData.ai_ready_data.activity_level, color: 'purple' },
          ].map(s => (
            <div key={s.label} className="bg-slate-800/50 rounded-lg p-3 text-center">
              <p className="text-xs text-slate-400">{s.label}</p>
              <p className={`text-lg font-bold text-${s.color}-400 capitalize`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}
    </div>

    {/* Plan Generator Form */}
    <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-2xl p-6 mb-8">
      <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
        <Target className="w-5 h-5 text-emerald-500" /> Select Your Goals
      </h2>

      <div className="mb-6">
        <label className="block text-sm text-slate-400 mb-3">Focus Areas (select multiple)</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {focusAreaOptions.map((area) => (
            <button key={area.id} onClick={() => toggleFocusArea(area.id)}
              className={`p-3 rounded-xl border transition-all text-left ${
                formData.focus_areas.includes(area.id)
                  ? 'bg-emerald-600/20 border-emerald-500 text-emerald-400'
                  : 'bg-slate-700/50 border-slate-600 hover:border-slate-500'
              }`}
              data-testid={`focus-area-${area.id}`}>
              <span className="text-xl mr-2">{area.icon}</span>
              <span className="text-sm font-medium">{area.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Age', key: 'age', type: 'number', placeholder: 'Enter age' },
          { label: 'Gender', key: 'gender', type: 'select', options: [['male','Male'],['female','Female'],['other','Other']] },
          { label: 'Activity Level', key: 'activity_level', type: 'select', options: [['sedentary','Sedentary'],['light','Light Activity'],['moderate','Moderate Activity'],['active','Active'],['very_active','Very Active']] },
        ].map(f => (
          <div key={f.key}>
            <label className="block text-sm text-slate-400 mb-2">{f.label}</label>
            {f.type === 'select' ? (
              <select value={formData[f.key]} onChange={(e) => setFormData(prev => ({...prev, [f.key]: e.target.value}))}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500">
                {f.options.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ) : (
              <input type={f.type} value={formData[f.key]} onChange={(e) => setFormData(prev => ({...prev, [f.key]: e.target.value}))}
                placeholder={f.placeholder} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[
          { label: 'Current Weight (kg)', key: 'current_weight_kg', placeholder: 'e.g., 70' },
          { label: 'Target Weight (kg)', key: 'target_weight_kg', placeholder: 'e.g., 65' },
          { label: 'Plan Duration', key: 'plan_duration', type: 'select', options: [['2_weeks','2 Weeks'],['4_weeks','4 Weeks'],['8_weeks','8 Weeks'],['12_weeks','12 Weeks']] },
        ].map(f => (
          <div key={f.key}>
            <label className="block text-sm text-slate-400 mb-2">{f.label}</label>
            {f.type === 'select' ? (
              <select value={formData[f.key]} onChange={(e) => setFormData(prev => ({...prev, [f.key]: e.target.value}))}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500">
                {f.options.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ) : (
              <input type="number" value={formData[f.key]} onChange={(e) => setFormData(prev => ({...prev, [f.key]: e.target.value}))}
                placeholder={f.placeholder} className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            )}
          </div>
        ))}
      </div>

      <button onClick={generatePlan} disabled={loading || formData.focus_areas.length === 0}
        className="w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid="generate-plan-btn">
        {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating Your Plan...</> : <><Sparkles className="w-5 h-5" /> Generate AI Lifestyle Plan</>}
      </button>

      {error && (
        <div className="mt-4 p-4 bg-red-500/20 border border-red-500 rounded-lg flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5" />{error}
        </div>
      )}
    </div>
  </>
);
